// Template storage utilities for polls and quizzes
const TEMPLATE_STORAGE_KEY = "poll_quiz_templates";

/**
 * Get all saved templates from localStorage
 * @returns {Array} Array of template objects
 */
export const getTemplates = () => {
  try {
    const templates = localStorage.getItem(TEMPLATE_STORAGE_KEY);
    return templates ? JSON.parse(templates) : [];
  } catch (error) {
    console.error("Error loading templates:", error);
    return [];
  }
};

/**
 * Save a template to localStorage
 * @param {Object} template - Template object to save
 * @returns {void}
 */
export const saveTemplate = template => {
  try {
    const templates = getTemplates();
    const existingIndex = templates.findIndex(t => t.id === template.id);

    const templateToSave = {
      ...template,
      updatedAt: new Date().toISOString(),
    };

    if (existingIndex >= 0) {
      templates[existingIndex] = templateToSave;
    } else {
      templates.push(templateToSave);
    }

    localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(templates));
  } catch (error) {
    console.error("Error saving template:", error);
    throw error;
  }
};

/**
 * Delete a template from localStorage
 * @param {string} templateId - ID of template to delete
 * @returns {void}
 */
export const deleteTemplate = templateId => {
  try {
    const templates = getTemplates();
    const filtered = templates.filter(t => t.id !== templateId);
    localStorage.setItem(TEMPLATE_STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error("Error deleting template:", error);
    throw error;
  }
};

/**
 * Export templates to a JSON file
 * @param {Array} templates - Array of templates to export (optional, exports all if not provided)
 * @returns {void}
 */
export const exportTemplates = (templates = null) => {
  try {
    const templatesToExport = templates || getTemplates();

    // Check if there are templates to export
    if (!templatesToExport || templatesToExport.length === 0) {
      throw new Error("No templates available to export");
    }

    // Clean templates: remove unnecessary fields
    const cleanedTemplates = templatesToExport.map(template => {
      const isPoll = template.type === "poll";

      // Remove fields that shouldn't be exported (id, createdAt, updatedAt, rolesThatCanViewResponses)
      const fieldsToRemove = [
        "id",
        "createdAt",
        "updatedAt",
        "rolesThatCanViewResponses",
      ];
      const templateWithoutMeta = Object.fromEntries(
        Object.entries(template).filter(
          ([key]) => !fieldsToRemove.includes(key)
        )
      );

      // For polls, also remove isCorrectAnswer from options
      const cleanedTemplate = {
        ...templateWithoutMeta,
        questions: template.questions.map(q => ({
          ...q,
          options: (q.options || []).map(opt => {
            if (isPoll) {
              // eslint-disable-next-line no-unused-vars
              const { isCorrectAnswer, ...optionWithoutCorrect } = opt;
              return optionWithoutCorrect;
            }
            // For quizzes, keep isCorrectAnswer
            return opt;
          }),
        })),
      };

      return cleanedTemplate;
    });

    const dataStr = JSON.stringify(cleanedTemplates, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `poll_quiz_templates_${
      new Date().toISOString().split("T")[0]
    }.json`;
    link.style.display = "none";
    document.body.appendChild(link);

    // Use setTimeout to ensure the click happens in the event loop
    // This helps with browser security restrictions
    setTimeout(() => {
      try {
        link.click();
        // Clean up after a short delay to ensure download starts
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 100);
      } catch (clickError) {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        throw new Error(
          "Failed to trigger download. Please check your browser's download settings."
        );
      }
    }, 0);
  } catch (error) {
    console.error("Error exporting templates:", error);
    throw error;
  }
};

/**
 * Import templates from a JSON file
 * @param {File} file - JSON file to import
 * @returns {Promise<Array>} Array of imported templates
 */
export const importTemplates = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = event => {
      try {
        const content = event.target.result;
        const importedTemplates = JSON.parse(content);

        // Validate that it's an array
        if (!Array.isArray(importedTemplates)) {
          reject(
            new Error("Invalid file format: Expected an array of templates")
          );
          return;
        }

        // Validate template structure
        const validatedTemplates = importedTemplates.map((template, index) => {
          if (
            !template.title ||
            !template.type ||
            !Array.isArray(template.questions)
          ) {
            throw new Error(
              "Invalid template structure: Missing required fields (title, type, or questions)"
            );
          }

          const isPoll = template.type === "poll";

          // Generate new ID and timestamps for imported templates
          // Use existing ID if valid, otherwise generate new one
          const newId =
            template.id && typeof template.id === "string"
              ? template.id + "_" + Date.now()
              : Date.now().toString() + Math.random().toString(36).substr(2, 9);

          return {
            ...template,
            id: newId,
            createdAt: template.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            questions: template.questions.map(q => ({
              ...q,
              options: (q.options || []).map(opt => {
                // For polls, always exclude isCorrectAnswer field since polls don't have correct answers
                if (isPoll) {
                  // eslint-disable-next-line no-unused-vars
                  const { isCorrectAnswer, ...optionWithoutCorrect } = opt;
                  return optionWithoutCorrect;
                }
                // For quizzes, keep isCorrectAnswer
                return opt;
              }),
            })),
          };
        });

        // Save imported templates
        const existingTemplates = getTemplates();
        const allTemplates = [...existingTemplates, ...validatedTemplates];
        localStorage.setItem(
          TEMPLATE_STORAGE_KEY,
          JSON.stringify(allTemplates)
        );

        resolve(validatedTemplates);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error("Error reading file"));
    reader.readAsText(file);
  });
};

/**
 * Import templates from a URL
 * @param {string} url - URL to fetch templates from
 * @returns {Promise<Array>} Array of imported templates
 */
export const importTemplatesFromUrl = url => {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(importedTemplates => {
        // Validate that it's an array
        if (!Array.isArray(importedTemplates)) {
          reject(
            new Error("Invalid file format: Expected an array of templates")
          );
          return;
        }

        // Validate template structure
        const validatedTemplates = importedTemplates.map((template, index) => {
          if (
            !template.title ||
            !template.type ||
            !Array.isArray(template.questions)
          ) {
            throw new Error(
              "Invalid template structure: Missing required fields (title, type, or questions)"
            );
          }

          const isPoll = template.type === "poll";

          // Generate new ID and timestamps for imported templates
          const newId =
            template.id && typeof template.id === "string"
              ? template.id + "_" + Date.now()
              : Date.now().toString() + Math.random().toString(36).substr(2, 9);

          return {
            ...template,
            id: newId,
            createdAt: template.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            questions: template.questions.map(q => ({
              ...q,
              options: (q.options || []).map(opt => {
                // For polls, always exclude isCorrectAnswer field since polls don't have correct answers
                if (isPoll) {
                  // eslint-disable-next-line no-unused-vars
                  const { isCorrectAnswer, ...optionWithoutCorrect } = opt;
                  return optionWithoutCorrect;
                }
                // For quizzes, keep isCorrectAnswer
                return opt;
              }),
            })),
          };
        });

        // Save imported templates
        const existingTemplates = getTemplates();
        const allTemplates = [...existingTemplates, ...validatedTemplates];
        localStorage.setItem(
          TEMPLATE_STORAGE_KEY,
          JSON.stringify(allTemplates)
        );

        resolve(validatedTemplates);
      })
      .catch(error => {
        // Check if it's a CORS error
        if (
          error.message.includes("CORS") ||
          error.message.includes("Failed to fetch") ||
          error.name === "TypeError"
        ) {
          reject(
            new Error(
              "CORS_ERROR: Unable to fetch from this URL due to CORS restrictions. Please download the file and use the 'From File' option instead."
            )
          );
        } else {
          reject(error);
        }
      });
  });
};

/**
 * Convert a poll/quiz to a template format
 * @param {Object} poll - Poll object from HMS
 * @param {Array} questions - Array of questions
 * @returns {Object} Template object
 */
export const pollToTemplate = (poll, questions) => {
  const isPoll = (poll.type || "poll") === "poll";

  return {
    id: poll.id || Date.now().toString(),
    title: poll.title,
    type: poll.type || "poll",
    anonymous: poll.anonymous || false,
    rolesThatCanViewResponses: poll.rolesThatCanViewResponses || [],
    questions: questions.map(q => ({
      text: q.text,
      type: q.type,
      options: (q.options || []).map(opt => {
        // For polls, always exclude isCorrectAnswer field since polls don't have correct answers
        if (isPoll) {
          // eslint-disable-next-line no-unused-vars
          const { isCorrectAnswer, ...optionWithoutCorrect } = opt;
          return optionWithoutCorrect;
        }
        // For quizzes, keep isCorrectAnswer
        return opt;
      }),
      skippable: q.skippable !== undefined ? q.skippable : true,
    })),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};
